import { R4 } from "@ahryman40k/ts-fhir-types";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

import { ConfidentialityCode } from "@/global";

import { FHIR_DATE_FORMAT, FORM_DATE_FORMAT } from "../constants";
import {
  AllergyIntoleranceFormModel,
  ImmunizationFormModel,
  PractitionerRoleForm,
  RiskFactorFormModel,
} from "../types";
import { getConfidentialityCode } from "./aggregation";
import {
  getPerformer,
  getRecorder,
  getResourceByReference,
  makeEntry,
  makeUuidUrnReference,
  sortNotes,
} from "./fhir";

dayjs.extend(customParseFormat);

export function practitionerRoleToFormState(
  practitionerRole: R4.IPractitionerRole | null,
  bundle: R4.IBundle
): PractitionerRoleForm {
  const practitionerRef = practitionerRole?.practitioner?.reference;
  const practitioner = practitionerRef
    ? (getResourceByReference(bundle, practitionerRef) as R4.IPractitioner)
    : null;
  const organizationRef = practitionerRole?.organization?.reference;
  const organization = organizationRef
    ? (getResourceByReference(bundle, organizationRef) as R4.IOrganization)
    : null;

  return {
    practitionerName: {
      prefix: practitioner?.name?.[0].prefix?.[0] ?? "",
      given: practitioner?.name?.[0].given?.[0] ?? "",
      family: practitioner?.name?.[0].family ?? "",
    },
    organizationName: organization?.name ?? "",
  };
}

export function practitionerRoleFormToBundleEntries(
  form: PractitionerRoleForm
): R4.IBundle_Entry[] {
  const practitionerDefined = isPractitionerDefined(form);
  const organizationDefined = isOrganizationDefined(form);

  if (!practitionerDefined && !organizationDefined) {
    throw new Error("Either the practitioner or the organization must be defined.");
  }

  const entries: R4.IBundle_Entry[] = [];

  const practitionerRole: R4.IPractitionerRole = {
    resourceType: "PractitionerRole",
    id: crypto.randomUUID(),
    meta: {
      profile: ["http://fhir.ch/ig/ch-core/StructureDefinition/ch-core-practitionerrole-epr"],
    },
    active: true,
  };

  entries.push(makeEntry(practitionerRole));

  if (isPractitionerDefined(form)) {
    const practitioner: R4.IPractitioner = {
      resourceType: "Practitioner",
      id: crypto.randomUUID(),
      meta: {
        profile: ["http://fhir.ch/ig/ch-core/StructureDefinition/ch-core-practitioner-epr"],
      },
      // TODO: identifier?
      name: [
        {
          family: form.practitionerName.family,
          given: [form.practitionerName.given],
          prefix: [form.practitionerName.prefix],
        },
      ],
    };

    entries.push(makeEntry(practitioner));
    practitionerRole.practitioner = makeUuidUrnReference(practitioner);
  }

  if (isOrganizationDefined(form)) {
    const organization: R4.IOrganization = {
      resourceType: "Organization",
      id: crypto.randomUUID(),
      meta: {
        profile: ["https://fhir.ch/ig/ch-core/StructureDefinition-ch-core-organization-epr"],
      },
      name: form.organizationName,
    };

    entries.push(makeEntry(organization));
    practitionerRole.organization = makeUuidUrnReference(organization);
  }

  return entries;
}

export function getPractitionerRoleReference(
  entries: R4.IBundle_Entry[]
): R4.IReference | undefined {
  const id = entries?.find((entry) => entry.resource?.resourceType === "PractitionerRole")?.resource
    ?.id;
  return id
    ? {
        reference: `urn:uuid:${id}`,
      }
    : undefined;
}

export function isPractitionerRoleFormValid(form?: PractitionerRoleForm): boolean {
  if (!form) {
    return false;
  }

  return isPractitionerDefined(form) || (isOrganizationDefined(form) && isPractitionerEmpty(form));
}

export function isEndDateValid(beginDate: string, endDate: string) {
  if (beginDate && endDate) {
    return dayjs(endDate, FORM_DATE_FORMAT).isAfter(dayjs(beginDate, FORM_DATE_FORMAT));
  }

  return true;
}

function isOrganizationDefined(form: PractitionerRoleForm): boolean {
  return !!form?.organizationName?.trim();
}

function isPractitionerDefined(form: PractitionerRoleForm): boolean {
  if (!form.practitionerName) {
    return false;
  }

  const { given, family } = form.practitionerName;

  return !!given.trim() && !!family.trim();
}

function isPractitionerEmpty(form: PractitionerRoleForm): boolean {
  if (!form.practitionerName) {
    return true;
  }

  const { prefix, given, family } = form.practitionerName;

  return !(prefix.trim() || given.trim() || family.trim());
}

export function toIsoDate(formDate: string): string {
  return dayjs(formDate, FORM_DATE_FORMAT).format(FHIR_DATE_FORMAT);
}

export function toIsoDateTime(formDate: string): string {
  return dayjs(formDate, FORM_DATE_FORMAT).toISOString();
}

export function immunizationToFormModel(
  immunization: R4.IImmunization,
  bundle: R4.IBundle
): ImmunizationFormModel {
  // NOTE: Like the original vaccination module, we assume this is always a PractitionerRole
  const performer = getPerformer(bundle, immunization);

  return {
    confidentialityCode: getConfidentialityCode(immunization) ?? ConfidentialityCode.Normal,
    date: immunization.occurrenceDateTime
      ? dayjs(immunization.occurrenceDateTime).format(FORM_DATE_FORMAT)
      : dayjs().format(FORM_DATE_FORMAT),
    vaccineCode: immunization.vaccineCode?.coding?.[0].code ?? null,
    targetDisease:
      immunization.protocolApplied?.[0].targetDisease
        ?.map((concept) => concept.coding?.[0].code || "")
        .filter((code) => !!code) ?? [],
    dose: immunization.protocolApplied?.[0].doseNumberPositiveInt ?? null,
    lotNumber: immunization.lotNumber ?? "",
    performer: practitionerRoleToFormState(performer, bundle),
    notes: sortNotes(immunization.note ?? []),
  };
}

export function conditionToFormModel(
  condition: R4.ICondition,
  bundle: R4.IBundle
): RiskFactorFormModel {
  // NOTE: Like the original vaccination module, we assume this is always a PractitionerRole
  const recorder = getRecorder(bundle, condition) as R4.IPractitionerRole;

  return {
    confidentialityCode: getConfidentialityCode(condition) ?? ConfidentialityCode.Normal,
    date: condition.recordedDate
      ? dayjs(condition.recordedDate).format(FORM_DATE_FORMAT)
      : dayjs().format(FORM_DATE_FORMAT),
    code: condition.code?.coding?.[0]?.code ?? "",
    beginDate: condition.onsetDateTime
      ? dayjs(condition.onsetDateTime).format(FORM_DATE_FORMAT)
      : "",
    endDate: condition.abatementDateTime
      ? dayjs(condition.abatementDateTime).format(FORM_DATE_FORMAT)
      : "",
    recorder: practitionerRoleToFormState(recorder, bundle),
    notes: sortNotes(condition.note ?? []),
    clinicalStatus: (condition.clinicalStatus?.coding?.[0]?.code ?? "active") as
      | "active"
      | "inactive",
  };
}

export function allergyIntoleranceToFormModel(
  allergyIntolerance: R4.IAllergyIntolerance,
  bundle: R4.IBundle
): AllergyIntoleranceFormModel {
  // NOTE: Like the original vaccination module, we assume this is always a PractitionerRole
  const recorder = getRecorder(bundle, allergyIntolerance) as R4.IPractitionerRole;

  return {
    confidentialityCode: getConfidentialityCode(allergyIntolerance) ?? ConfidentialityCode.Normal,
    date: allergyIntolerance.recordedDate
      ? dayjs(allergyIntolerance.recordedDate).format(FORM_DATE_FORMAT)
      : dayjs().format(FORM_DATE_FORMAT),
    code: allergyIntolerance.code?.coding?.[0]?.code ?? "",
    recorder: practitionerRoleToFormState(recorder, bundle),
    notes: sortNotes(allergyIntolerance.note ?? []),
  };
}
